'use client';

import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect, useRef } from 'react';

import { getVid } from '@/context';
import { EngagementSignalData, TRACKING } from '@/helpers/tracking';
import { LDUser } from '@/models';

import { useLogger } from './useLogger';
import { saveVisitorActivity } from './useTracking';

/**
 * Records a passive `engagement_confirmed` visitor activity the first time a real human genuinely
 * interacts with the page (a tap or key press). Facebook in-app pre-warms load and fire our tracking
 * with no human present, so the *presence* of this row is the un-fakeable "a real person was here"
 * signal — used to report conversion on a pre-warm-excluded denominator (CONS-2436).
 *
 * Deliberately bypasses `trackEvent` so it does NOT fan out to the ad platforms (Google/Meta) — this is
 * measurement only and must not pollute those platforms. Writes at most once per page load.
 */
export const useEngagementSignals = () => {
  const ldClient = useLDClient();
  const { addAction, addError } = useLogger();
  const firedRef = useRef(false);

  useEffect(() => {
    const options = { capture: true, passive: true } as const;

    const handleFirstInteraction = (event: Event) => {
      const hasBeenActive = window.navigator.userActivation?.hasBeenActive;
      if (firedRef.current || !(hasBeenActive ?? event.isTrusted)) {
        return;
      }
      firedRef.current = true;

      const context = ldClient?.getContext() as LDUser | undefined;
      if (context?.isTester) {
        return;
      }

      const eventData: EngagementSignalData = {
        has_been_active: hasBeenActive ?? true,
        time_to_first_interaction_ms: Math.round(performance.now()),
      };

      // Datadog RUM action for dashboard visibility; still bypasses trackEvent's ad-platform fan-out.
      addAction(TRACKING.ENGAGEMENT_CONFIRMED, eventData);
      saveVisitorActivity(getVid(), TRACKING.ENGAGEMENT_CONFIRMED, eventData).catch((error) => addError(error));
    };

    window.addEventListener('pointerdown', handleFirstInteraction, options);
    window.addEventListener('keydown', handleFirstInteraction, options);

    return () => {
      window.removeEventListener('pointerdown', handleFirstInteraction, options);
      window.removeEventListener('keydown', handleFirstInteraction, options);
    };
  }, [addAction, addError, ldClient]);
};
